/**
 * Variant-direction proposals (#909) — the shared shape between the
 * propose-variant-options Trigger task (writer), the job-status route
 * (transport), and the variant-steering modal (reader).
 *
 * Lives in validators/ rather than next to the prompt so the client bundle
 * can import the type + parser without dragging prompt text along.
 */

import { z } from 'zod';

/** One concrete adaptation direction for a target variant. */
export interface VariantOption {
  /** Stable kebab-case id, unique within one proposal set. */
  id: string;
  /** The dish component the direction touches (e.g. "Pickled vegetables"). */
  component: string;
  /** Short label for the card (e.g. "24 h rice-vinegar đồ chua"). */
  title: string;
  /** The specific change: named ingredient swaps and/or technique. */
  description: string;
  /** What it buys the eater — flavour, texture, or time. */
  payoff: string;
  /**
   * The same direction in one English sentence, for the English-only
   * derivation prompt. Present only when the display fields are NOT English
   * (#956) — an English proposal is already its own direction line.
   */
  directionEn?: string;
}

export const variantOptionSchema = z.object({
  id: z
    .string()
    .trim()
    .min(1)
    .max(64)
    // Normalise whatever the model emits into a safe slug — ids only need to
    // be unique within one proposal set, they never leave the modal.
    .transform((s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'option'),
  component: z.string().trim().min(1).max(80),
  title: z.string().trim().min(1).max(90),
  description: z.string().trim().min(1).max(400),
  payoff: z.string().trim().min(1).max(200),
  directionEn: z.string().trim().min(1).max(400).optional(),
});

export const variantOptionsResultSchema = z.object({
  options: z.array(variantOptionSchema).min(3).max(6),
});

/**
 * Parse the `variantOptions` blob a completed propose_variant_options job
 * stored in `output_data`. Returns null when absent or malformed — callers
 * treat that as a failed proposal, never as an empty success.
 */
export function parseStoredVariantOptions(value: unknown): VariantOption[] | null {
  const parsed = z.array(variantOptionSchema).min(1).max(6).safeParse(value);
  if (!parsed.success) return null;
  // Dedupe ids defensively — the UI keys decisions by id.
  const seen = new Set<string>();
  return parsed.data.map((opt, i) => {
    const id = seen.has(opt.id) ? `${opt.id}-${i}` : opt.id;
    seen.add(id);
    return { ...opt, id };
  });
}

/**
 * Render an option as the single-line direction string the derive prompt
 * receives for ACCEPTED/REJECTED lists — self-contained so the derivation
 * needs no access to the proposal job.
 */
export function variantOptionToDirection(opt: VariantOption): string {
  // A German-displayed proposal carries its own English restatement — the
  // derivation prompt is English-only and its language guard rejects German
  // fields, so never hand it the card copy (#956).
  if (opt.directionEn) return opt.directionEn;
  return `${opt.component} — ${opt.title}: ${opt.description}`;
}
